import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';
import { useCart } from '../contexts/CartContext';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { openCart, itemCount } = useCart();
  const location = useLocation();
  
  const navLinks = [
    { path: '/', label: 'home' },
    { path: '/products', label: 'products' },
    { path: '/delivery', label: 'delivery' },
    { path: '/about', label: 'about' },
    { path: '/contact', label: 'contact' },
  ];
  
  const isActive = (path) => location.pathname === path;

  // Verre sombre tant que l'en-tête survole une image sombre (le hero de
  // l'accueil), verre clair ensuite. Sans cela, le bandeau blanc coupait le
  // hero en deux ; et un verre sombre partout rendrait illisibles les pages
  // à fond clair.
  const [surImage, setSurImage] = useState(false);

  const evaluerFond = useCallback(() => {
    const hero = document.querySelector('[data-hero]');
    if (!hero) {
      setSurImage(false);
      return;
    }
    // On bascule quand le bas du hero atteint le bas de l'en-tête.
    const hauteurEntete = window.innerWidth >= 1024 ? 80 : 64;
    setSurImage(hero.getBoundingClientRect().bottom > hauteurEntete);
  }, []);

  useEffect(() => {
    evaluerFond();
    window.addEventListener('scroll', evaluerFond, { passive: true });
    window.addEventListener('resize', evaluerFond);
    return () => {
      window.removeEventListener('scroll', evaluerFond);
      window.removeEventListener('resize', evaluerFond);
    };
  }, [evaluerFond, location.pathname]);

  // Le menu mobile déroulant est opaque : ouvert, l'en-tête reprend son fond clair.
  const verreSombre = surImage && !mobileMenuOpen;
  const surLeFond = (clair, sombre) => (verreSombre ? sombre : clair);
  
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl backdrop-saturate-150 border-b transition-colors duration-500 ${
        verreSombre
          ? 'bg-text_primary/25 border-white/15'
          : 'bg-white/70 border-black/5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <Logo className="h-9 lg:h-11 w-auto" couleur={verreSombre ? '#ffffff' : null} />
            <div className="min-w-0">
              <h1 className={`text-base sm:text-lg lg:text-xl font-bold font-heading tracking-tight whitespace-nowrap transition-colors duration-500 ${surLeFond('text-text_primary', 'text-white')}`}>AfriOne BD</h1>
              <p className={`hidden sm:block text-xs transition-colors duration-500 ${surLeFond('text-text_secondary', 'text-white/70')}`}>African Grocery</p>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label}`}
                className={`relative font-medium text-sm uppercase tracking-widest transition-colors duration-300 ${
                  isActive(link.path)
                    ? surLeFond('text-primary', 'text-white')
                    : surLeFond('text-text_secondary hover:text-primary', 'text-white/70 hover:text-white')
                }`}
              >
                {t(link.label)}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="nav-underline"
                    className={`absolute -bottom-1 left-0 right-0 h-0.5 rounded-full ${surLeFond('bg-primary', 'bg-white')}`}
                  />
                )}
              </Link>
            ))}
          </nav>
          
          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-300 ${surLeFond('border-border hover:bg-secondary/50', 'border-white/30 hover:bg-white/10')}`}
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe className={`w-4 h-4 transition-colors duration-500 ${surLeFond('text-text_secondary', 'text-white/80')}`} />
              <span className={`text-sm font-medium uppercase transition-colors duration-500 ${surLeFond('text-text_primary', 'text-white')}`}>{language}</span>
            </button>
            
            {/* Cart Button */}
            <button
              onClick={openCart}
              className={`relative p-2 rounded-full transition-all duration-300 ${surLeFond('hover:bg-secondary/50', 'hover:bg-white/10')}`}
              data-testid="cart-button"
              aria-label="Open cart"
            >
              <ShoppingCart className={`w-6 h-6 transition-colors duration-500 ${surLeFond('text-text_primary', 'text-white')}`} />
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center"
                >
                  {itemCount}
                </motion.span>
              )}
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 rounded-full transition-all duration-300 ${surLeFond('hover:bg-secondary/50', 'hover:bg-white/10')}`}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen
                ? <X className={`w-6 h-6 ${surLeFond('text-text_primary', 'text-white')}`} />
                : <Menu className={`w-6 h-6 ${surLeFond('text-text_primary', 'text-white')}`} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-border overflow-hidden"
          >
            <nav className="py-4 px-4 space-y-2">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label}`}
                  className={`block px-4 py-3 rounded-xl font-medium transition-colors duration-300 ${
                    isActive(link.path) 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-text_secondary hover:bg-secondary/50'
                  }`}
                >
                  {t(link.label)}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
